import { motion } from 'framer-motion';
import { Globe, Heart, Home, Download, AlertTriangle, Landmark, Clock, Users, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const continents = [
  {
    name: "North America",
    location: "Washington, D.C.",
    subtitle: "Vietnam Veterans Memorial — Honoring Permanence Through Stone",
    description: "Vietnam Memorial - honoring permanence through stone",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/gjtgz9yh_download%20%2812%29%20-%20Copy.jpg",
    article: `The Vietnam Veterans Memorial in Washington, D.C. stands as one of the most powerful examples of how stone can hold memory with a clarity no digital medium can match. Its polished granite surface reflects the viewer, merging the living with the names of the fallen, creating a dialogue across time. This mirrors the core purpose of the Global Stone Initiative: to create installations where human achievement, sacrifice, and knowledge are preserved in a medium that endures. As your document notes, stone "has endured for millennia… providing a resilient record of civilization," and the Memorial demonstrates this principle in a modern context. It shows how carved stone can carry emotional, historical, and cultural weight long after the technologies of any given era fade.

Placing a Global Stone Initiative installation in North America, particularly near a site like this, anchors the project in a region that already understands the power of stone as a vessel for collective memory. The Vietnam Memorial's minimalist design and reflective surfaces parallel the Initiative's goal of merging ancient craft with modern interpretation. It becomes a natural symbolic partner: a reminder that stone can speak, stone can teach, and stone can hold the stories we refuse to let disappear. This site reinforces the Initiative's mission to create "a permanent record of human achievement" that remains accessible to future generations without reliance on fragile digital systems.`
  },
  {
    name: "Africa",
    location: "Egypt",
    subtitle: "Pyramids — Ancient Stone Traditions Spanning Millennia",
    description: "Pyramids - ancient stone traditions spanning millennia",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/o74tbcah_1x1_in_that_time_egypt_and_America_w%20%282%29%20-%20Copy.png",
    article: `Africa, and Egypt in particular, represents the deepest lineage of humanity's stone based knowledge systems. The pyramids are not merely monuments; they are engineered archives, encoding astronomical alignments, cultural beliefs, and architectural mastery in stone that has survived thousands of years. Your document emphasizes that stone "resists decay while remaining universally interpretable across cultures and epochs," and nowhere is that more evident than in Egypt's monumental heritage. The pyramids prove that stone is not just durable, it is communicative, symbolic, and capable of transmitting meaning across civilizations.

By situating a Global Stone Initiative installation in Africa, the project reconnects modern humanity with its oldest traditions of permanence. The Initiative's animated stone sequences, "stone films" carved into granite and basalt, become a contemporary continuation of the same impulse that drove ancient artisans to encode knowledge in stone. Africa's site becomes a bridge between the earliest human archives and the future of global knowledge preservation. It honors the continent's foundational role in human history while contributing a new chapter to the story of stone as humanity's most enduring medium.`
  },
  {
    name: "Australia",
    location: "Outback",
    subtitle: "Sacred Rock Formations and Aboriginal Heritage",
    description: "Sacred rock formations and Aboriginal heritage",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/kwwe4djz_images%20%281%29%20-%20Copy.jpeg",
    article: `Australia's Outback contains some of the oldest continuous cultural traditions on Earth, expressed through rock art, sacred formations, and landscape based storytelling. Aboriginal cultures have long used stone surfaces as living archives, repositories of cosmology, law, and ancestral memory. This aligns directly with the Initiative's mission to create installations that "inspire, educate, and unify communities worldwide" through durable, culturally resonant mediums. The Outback's stone heritage demonstrates that knowledge carved into the land itself can survive tens of thousands of years.

A Global Stone Initiative installation in Australia would honor this lineage by integrating modern optical animation with ancient traditions of stone based storytelling. The site becomes a dialogue between Indigenous knowledge systems and global technological innovation. It reinforces the Initiative's commitment to cultural collaboration, as your document highlights: "Engagement with local artists and educators to adapt content to cultural contexts." Australia's contribution to the global network is therefore not just geographic, it is philosophical, reminding the world that stone is not merely a material but a medium of identity, continuity, and belonging.`
  },
  {
    name: "Europe",
    location: "Greece",
    subtitle: "Antikythera Mechanism — Ancient Precision Engineering",
    description: "Antikythera mechanism - ancient precision engineering",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/wo9jcp8f_antikythera-mechanism-reconstruction-front%20-%20Copy.webp",
    article: `Europe's site, centered on Greece, draws its symbolic power from the Antikythera mechanism, an ancient analog computer carved and assembled with astonishing precision. This artifact represents the fusion of engineering, astronomy, and craftsmanship, echoing the Initiative's own blend of "ancient craft with modern optics." The mechanism proves that ancient technologies were not primitive; they were sophisticated, durable, and deeply intentional. It embodies the spirit of human ingenuity that the Global Stone Initiative seeks to preserve in permanent form.

Placing an installation in Europe ties the project to a lineage of scientific and philosophical advancement. Greece's intellectual heritage, mathematics, logic, engineering, aligns with the Initiative's goal of creating encyclopedic stone archives that transcend digital fragility. As your document states, the project aims to "record encyclopedic knowledge and cultural milestones" in a medium that endures. Europe's site becomes a tribute to the continuity of human innovation, linking ancient mechanical genius to modern optical animation carved in stone.`
  },
  {
    name: "Asia",
    location: "Archaeological Sites",
    subtitle: "Stone Archives Preserving Ancient Knowledge",
    description: "Stone archives preserving ancient knowledge",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/8pdwwxa0_download%20%283%29-zog2%20-%20Copy.jpg",
    article: `Asia's vast archaeological record, from India to Japan to China, contains some of the world's most intricate stone inscriptions, temples, and knowledge systems. These sites demonstrate how stone has been used not only for art and architecture but for mathematics, astronomy, governance, and philosophy. Your document notes that stone remains "universally interpretable across cultures and epochs," and Asia's stone archives exemplify this universality. They show how civilizations across the continent independently recognized stone as the most reliable medium for transmitting knowledge.

A Global Stone Initiative installation in Asia would honor this deep tradition by introducing a new form of stone based communication: animated sequences carved with precision tools and viewed through optical apertures. This merges Asia's historical mastery of stone with cutting edge fabrication techniques like CAD, CNC, and lithography mentioned in your proposal. The site becomes a living archive, one that respects ancient knowledge systems while contributing a new technological chapter to the continent's long history of stone encoded wisdom.`
  },
  {
    name: "South America",
    location: "Brazil / Chile",
    subtitle: "Universities and Indigenous Heritage Centers",
    description: "Universities and indigenous heritage centers",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/o74tbcah_1x1_in_that_time_egypt_and_America_w%20%282%29%20-%20Copy.png",
    article: `South America offers a unique convergence of modern scholarship and ancient Indigenous knowledge systems. Universities in Brazil and Chile are deeply engaged in cultural preservation, archaeology, and environmental research, while Indigenous heritage centers maintain traditions that stretch back thousands of years. This duality aligns perfectly with the Initiative's mission to create installations that are both educational and culturally grounded. As your document emphasizes, the project "fosters collaboration between technology firms, cultural institutions, and local communities," and South America embodies that collaborative spirit.

A Global Stone Initiative installation here would serve as a bridge between academic research and ancestral knowledge. It would provide a permanent, public archive that honors Indigenous traditions of stonework while integrating modern optical animation. This reinforces the Initiative's goal of democratizing access to knowledge by placing installations in public, culturally significant spaces. South America's site becomes a testament to the continent's intellectual diversity and its commitment to preserving human achievement in durable, meaningful forms.`
  },
  {
    name: "Antarctica",
    location: "Research Station",
    subtitle: "Symbolic Installation Representing Humanity's Reach",
    description: "Symbolic installation representing humanity's reach",
    image: "https://customer-assets.emergentagent.com/job_charity-hub-82/artifacts/gjtgz9yh_download%20%2812%29%20-%20Copy.jpg",
    article: `Antarctica represents the frontier of human exploration, a continent dedicated entirely to science, cooperation, and the pursuit of knowledge. Installing a Global Stone Initiative monument at a research station symbolizes humanity's collective reach across the planet. It embodies the Initiative's global ambition, fulfilling the document's vision of "a global network of installations… distributed equitably across regions." Antarctica's harsh environment also underscores the value of stone as a medium that can withstand extreme conditions where digital systems may fail.`
  }
];

const budgetItems = [
  { item: "Prototype Development", amount: "$300,000" },
  { item: "Pilot Installations (5 sites)", amount: "$750,000" },
  { item: "Global Expedition & Logistics", amount: "$2,500,000" },
  { item: "Partnerships & Education", amount: "$750,000" },
  { item: "Administration & Staffing", amount: "$700,000" },
  { item: "Total Funding Requested", amount: "$5,000,000", isTotal: true }
];

const phases = [
  {
    title: "Phase 1 — Year 1",
    items: [
      "Prototype development (carved panels, aperture frames)",
      "Pilot installations at select museums",
      "Partnership establishment with universities"
    ]
  },
  {
    title: "Phase 2 — Years 2-3",
    items: [
      "Global expansion to diverse regions",
      "Launch educational programs",
      "Document and digitize monuments for online access"
    ]
  },
  {
    title: "Phase 3 — Years 4-5",
    items: [
      "Full global expedition",
      "Secure long-term sustainability",
      "Establish endowments and partnerships"
    ]
  }
];

export default function StoneInitiativePage() {
  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="stone-initiative-page">
      <div className="container-custom">
        
        {/* Support CTA Top */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16 p-8 rounded-2xl bg-gradient-to-br from-accent/10 to-secondary/10 border border-accent/20"
        >
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>Support This Work</h2>
          <p className="text-muted-foreground mb-4">Help fund research, publications, and global initiatives</p>
          <Link to="/donate">
            <Button className="btn-primary">
              <Heart className="w-4 h-4 mr-2" />
              Donate Now
            </Button>
          </Link>
        </motion.div>

        {/* Abstract */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>Abstract</h2>
          <Card className="bg-card border-border/50">
            <CardContent className="p-8">
              <p className="text-lg text-muted-foreground leading-relaxed">
                The Lithic Memory Initiative preserves humanity's collective achievements in a permanent, tangible medium by combining ancient stone carving traditions with optical animation techniques.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                Through a global network of installations recording encyclopedic knowledge and cultural milestones, viewers experience "stone films" — sequential carvings animated through specially designed apertures.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                This initiative represents both a technological enterprise and a cultural legacy, offering a novel approach to knowledge preservation in the face of digital fragility.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* The Problem */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>The Problem</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-xl font-bold mb-3">Digital Fragility</h3>
                <p className="text-muted-foreground">
                  Digital archives are fragile — prone to obsolescence, data loss, and limited accessibility. Libraries and archives struggle with metadata complexity and technological obsolescence. Humanity's greatest achievements risk disappearing without an enduring medium.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mb-4">
                  <Landmark className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="text-xl font-bold mb-3">Stone's Endurance</h3>
                <p className="text-muted-foreground">
                  Stone has preserved knowledge for millennia — from hieroglyphs to petroglyphs. It resists decay and remains universally interpretable across cultures and epochs. The Lithic Memory Initiative builds upon this tradition with modern innovation.
                </p>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Methodology */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>Methodology</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-3 text-accent">Design Principles</h3>
                <p className="text-muted-foreground text-sm">
                  Encyclopedic entries carved into durable granite and basalt. Sequential carvings designed to animate when viewed through apertures, inspired by zoetrope and parallax techniques.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-3 text-accent">Global Deployment</h3>
                <p className="text-muted-foreground text-sm">
                  Non-profit expedition team coordinating with cultural institutions, museums, and public spaces. Installations distributed equitably across regions for global accessibility.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-3 text-accent">Collaborations</h3>
                <p className="text-muted-foreground text-sm">
                  Partnerships with engineers, stoneworkers, optical designers. Local artists and educators engaged to adapt content to cultural contexts.
                </p>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Seven Continental Installations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-2 text-center" style={{ fontFamily: 'Playfair Display' }}>Seven Continental Installations</h2>
          <p className="text-center text-muted-foreground mb-12">A global network preserving human achievement across all continents.</p>
          
          <div className="space-y-12">
            {continents.map((continent, index) => (
              <motion.div
                key={continent.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <Card className="bg-card border-border/50 overflow-hidden">
                  <div className={`grid md:grid-cols-3 gap-0 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                    {/* Image */}
                    <div className={`aspect-video md:aspect-auto overflow-hidden ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                      <img 
                        src={continent.image} 
                        alt={continent.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Content */}
                    <div className={`md:col-span-2 p-6 md:p-8 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs uppercase tracking-wider text-accent font-bold">{continent.name}</span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {continent.location}
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
                        {continent.subtitle}
                      </h3>
                      <div className="text-sm text-muted-foreground leading-relaxed space-y-4">
                        {continent.article.split('\n\n').map((paragraph, i) => (
                          <p key={i}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Funding Request */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>Funding Request: $5,000,000</h2>
          <Card className="bg-card border-accent/30 max-w-2xl mx-auto">
            <CardContent className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground">Budget Item</th>
                    <th className="text-right py-2 text-muted-foreground">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetItems.map((item, index) => (
                    <tr key={index} className={`border-b border-border/50 ${item.isTotal ? 'font-bold text-accent' : ''}`}>
                      <td className="py-3">{item.item}</td>
                      <td className="py-3 text-right">{item.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </motion.div>

        {/* Implementation Plan */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>Implementation Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {phases.map((phase, index) => (
              <Card key={index} className="bg-card border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-accent" />
                    <h3 className="font-bold">{phase.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {phase.items.map((item, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Download Full Proposal */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="bg-card border-secondary/30 max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>Full Proposal Document</h3>
              <p className="text-muted-foreground mb-6">Download the complete Global Stone Initiative proposal with all details.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/a51x6leq_global%20stone%20initative%20.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-2">
                    <Download className="w-4 h-4" />
                    Download PDF
                  </Button>
                </a>
                <a href="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/o32ufx4q_global%20stone%20initative%20.docx" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-2">
                    <Download className="w-4 h-4" />
                    Download DOCX
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Support CTA Bottom */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 p-8 rounded-2xl bg-gradient-to-br from-accent/10 to-secondary/10 border border-accent/20"
        >
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>Support This Work</h2>
          <p className="text-muted-foreground mb-4">Help fund research, publications, and global initiatives</p>
          <Link to="/donate">
            <Button className="btn-primary">
              <Heart className="w-4 h-4 mr-2" />
              Donate Now
            </Button>
          </Link>
        </motion.div>

        {/* Navigation Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4"
        >
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Link to="/publications">
            <Button variant="outline" className="gap-2">
              <Globe className="w-4 h-4" />
              View Publications
            </Button>
          </Link>
          <Link to="/donate">
            <Button className="btn-primary gap-2">
              <Heart className="w-4 h-4" />
              Support / Donate
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
